import { Button, IconButton, Typography } from "@material-ui/core"
import VisibilityIcon from "@material-ui/icons/Visibility"
import { createColumnHelper } from "@tanstack/react-table"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import { useContext, useEffect, useState } from "react"
import { useInfiniteQuery } from "react-query"
import { courseAnalytics } from "~/actions/courses"
import AnalyticsTable from "~/components/Analytics/AnalyticsTable"
import BasicSortHeader from "~/components/Analytics/BasicSortHeader"
import AppContext from "~/contexts/App"
import CourseContext from "~/contexts/Course"
import usePermissionsRedirect from "~/hooks/usePermissionsRedirect"
import Page from "~/pages/Page"
import Router from "~/router"

import Box from "../../../../../../components/Common/Box"
import useStyles from "./styles"
const columnHelper = createColumnHelper()

const columns = [
  columnHelper.accessor((row) => row.user_id, {
    user_id: "user_id",
    header: ({ column }) => <BasicSortHeader column={column} label="User ID" />,
    id: "user_id",
  }),
  columnHelper.accessor((row) => row?.first_name, {
    first_name: "first_name",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="First Name" />
    ),
    id: "first_name",
    cell: (props) => {
      const splitName = props?.row?.original?.first_name?.split(" ")
      return (
        <span className="flex flex-col">
          {splitName?.map((sso, i) => (
            <span key={i}>{sso}</span>
          ))}
        </span>
      )
    },
  }),
  columnHelper.accessor((row) => row.last_name, {
    last_name: "last_name",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Last Name" />
    ),
    id: "last_name",
  }),
  columnHelper.accessor((row) => row.username, {
    username: "username",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Username" />
    ),
    id: "username",
  }),
  columnHelper.accessor((row) => row.email, {
    email: "email",
    header: ({ column }) => <BasicSortHeader column={column} label="Email" />,
    id: "email",
  }),
  columnHelper.accessor((row) => row.test_user, {
    test_user: "test_user",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Test User" />
    ),
    id: "test_user",
  }),
  columnHelper.accessor((row) => row.created, {
    created: "created",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Start Date" />
    ),
    id: "created",
    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.created === null
            ? "-"
            : dayjs(props.row.original.created).format("MM/DD/YYYY H:mm")}
        </Typography>
      )
    },
  }),
  columnHelper.accessor((row) => row.last_progress, {
    course_progress: "last_progress",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Last Progress" />
    ),
    id: "last_progress",
    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.last_progress === null
            ? "-"
            : dayjs(props.row.original.last_progress)?.format(
                "MM/DD/YYYY H:mm",
              )}
        </Typography>
      )
    },
  }),
  columnHelper.accessor((row) => row.completion_date, {
    completion_date: "completion_date",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Completion Date" />
    ),
    id: "completion_date",
    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.completion_date === null
            ? "-"
            : dayjs(props.row.original.completion_date).format(
                "MM/DD/YYYY H:mm",
              )}
        </Typography>
      )
    },
  }),

  columnHelper.accessor((row) => row.completed_lessons, {
    completed_lessons: "completed_lessons",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Completed Lessons" />
    ),
    id: "completed_lessons",
  }),
  columnHelper.accessor((row) => row.course_progress, {
    course_progress: "course_progress",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Course Progress" />
    ),
    id: "course_progress",
    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.course_progress === null
            ? "-"
            : props.row.original.course_progress}
          %
        </Typography>
      )
    },
  }),

  columnHelper.accessor((row) => row.course_status, {
    course_status: "course_status",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Course Status" />
    ),
    id: "course_status",
    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.course_status}
        </Typography>
      )
    },
  }),
  columnHelper.accessor((row) => row.course_id, {
    course_id: "course_id",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Course ID" />
    ),
    id: "course_id",
  }),

  columnHelper.accessor((row) => row.assessment_attempts, {
    assessment_attempts: "assessment_attempts",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Assessment Attempts" />
    ),
    id: "assessment_attempts",
  }),
  columnHelper.accessor((row) => row.passed_assessment, {
    passed_assessment: "passed_assessment",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Passed Assessments" />
    ),
    id: "passed_assessments",
  }),
  columnHelper.accessor((row) => row.best_assessment_score, {
    best_assessment_score: "best_assessment_score",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Best Assessment Score" />
    ),
    id: "best_assessment_score",
    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.best_assessment_score === null
            ? "-"
            : props.row.original.best_assessment_score}
        </Typography>
      )
    },
  }),
  columnHelper.accessor((row) => row.time_on_course, {
    time_on_course: "time_on_course",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Time on Course" />
    ),
    id: "time_on_course",
    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.time_on_course === null
            ? "-"
            : props.row.original.time_on_course}
        </Typography>
      )
    },
  }),
  columnHelper.accessor((row) => row.unique_days_on_course, {
    unique_days_on_course: "unique_days_on_course",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Unique Days on Course" />
    ),
    id: "unique_days_on_course",
  }),

  columnHelper.accessor((row) => row.in_progress_courses, {
    in_progress_courses: "in_progress_courses",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Courses in Progress" />
    ),
    id: "in_progress_courses",
    cell: (props) => {
      return (
        <span>
          {
            props.row.original.progressCourses?.length // '25/01/2019'
          }
        </span>
      )
    },
  }),
  columnHelper.accessor((row) => row.version, {
    version: "version",
    header: ({ column }) => <BasicSortHeader column={column} label="Version" />,
    id: "version",
  }),
]

const Analytics = ({ routes = [], ...rest }) => {
  const classes = useStyles()
  usePermissionsRedirect("/courses", "analytics")

  const { analyticsMutation, downloadAnalyticsMutation, course } =
    useContext(CourseContext)
  const { openDialog } = useContext(AppContext)
  const [searchValue, setSearchValue] = useState("")

  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)

  const [courseDuration, setCourseDuration] = useState(0)

  // looks through course and puts each lesson's video duration in an array
  const lessonDurations = () => {
    let durationArray = []
    //map through each course's lessons array
    course?.lessons?.map((lesson) => {
      if (lesson?.video && Object.keys(lesson?.video).length > 0) {
        // if a lesson has a video, pushing the duration (in s) to the array
        durationArray.push(
          lesson?.video?.duration || lesson?.video[0]?.duration,
        )
      }
    })

    // sumDurations adds each value in the array - gives total duration (in s)
    const sumDurations = durationArray.reduce((a, b) => a + b, 0)
    return sumDurations
  }

  const openResetProgress = (userData) => {
    openDialog("resetCourseProgress", userData)
  }

  // on initial load of card, set state variable
  useEffect(() => {
    setCourseDuration(lessonDurations())
  }, [course])
  let dataFetch = {
    searchValue: searchValue.length === 0 ? null : searchValue,
    limit: pageSize,
  }

  useEffect(() => {
    refetch()
  }, [course?.id])

  const { data, fetchNextPage, isFetching, refetch, hasNextPage } =
    useInfiniteQuery({
      queryKey: ["users", dataFetch],
      queryFn: async ({ pageParam = 0 }) => {
        return await courseAnalytics(course?.id, {
          ...dataFetch,
          offset: pageParam,
        })
      },
      getNextPageParam: (lastPage, allPages) => {
        const nextPageData = lastPage.nextOffset
        return nextPageData
      },
    })

  const columnActions = (row) => {
    return (
      <td className="flex relative pr-3">
        <IconButton
          key={row.original.user_id}
          onClick={() => openDialog("userDetailsDialog", row.original)}
          color="secondary"
          className="h-full"
        >
          <VisibilityIcon />
        </IconButton>
        {row.original.test_user === 1 ? (
          <Box display="flex" justifyContent="center">
            <Button
              color="secondary"
              onClick={() => openResetProgress(row.original)}
            >
              Reset
            </Button>
          </Box>
        ) : null}
      </td>
    )
  }

  return (
    <Page>
      <Box className={classes.container} width="100%">
        {/* <AnalyticsFilters type={"course"} /> */}
        <AnalyticsTable
          columns={columns}
          data={data}
          mutation={analyticsMutation}
          downloadMutation={downloadAnalyticsMutation}
          type={"course"}
          contentData={{ ...course, duration: courseDuration }}
          initialSortString={"created"}
          setSearchValue={setSearchValue}
          searchValue={searchValue}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          refetch={refetch}
          hasNextPage={hasNextPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          columnActions={columnActions}
        />
        <Router routes={routes} {...rest} />
      </Box>
    </Page>
  )
}

Analytics.propTypes = {
  routes: PropTypes.array,
}

export default Analytics
